import { React, Component, useState, useEffect, useRef, useContext } from "react"
import { createPortal } from 'react-dom'
import { Tooltip } from "@mui/material"
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { _Q } from "../query/queries"
import { activateMenu, checkUpdateNeeded, c_Fn, setItemDB, getItemDB,uuidv4, getProjectKey } from "./commons";
import { ThemeContext } from "./theme-toggler/theme-context";
import { loadingProgress } from "./APPF_ICONS"
import PageHeader from "./page-header/PageHeader";
import PageHeaderRefresh from "./page-header/RefreshButton";
import { NavSecurity } from "../orion/assets/icons/navIcons";
import useProjectStore from "../store/projectStore";
import useCoreStore from "../store/coreStore";

/* the dashboard's own thousands separator, so a count reads the same on every
   band that carries one */
const fmtCount = (n) => Number(n || 0).toLocaleString();
import { useEnsureHosts, useEnsureApplications, useEnsureInstalledPackages } from "../store/useCoreData";
import "./designer/Designer.css";
function createPortal1(children, mountNode) {
  let res = createPortal(children, mountNode)
  return res
}

export class IFrame extends Component {
  constructor(props) {
    super(props)
    this.state = {
      mountNode: null
    }
    this.setContentRef = (contentRef) => {
      this.setState({
        mountNode: contentRef?.contentWindow?.document?.body,
        cw: contentRef?.contentWindow
      })
    }
  }

  render() {
    const { children, ...props } = this.props
    const { mountNode } = this.state


    return (
      <iframe
        {...props}
        ref={this.setContentRef}
      >
        {mountNode && createPortal1(children, mountNode)}
      </iframe>
    )
  }
}

/**
 * A control the 3D view owns, drawn in the page header.
 *
 * The node itself stays in the view: its handlers are that document's React,
 * delegated to its own root, so a node moved out here would draw but never
 * respond. The header carries a live copy instead - re-cloned whenever the
 * original changes, which is what keeps the severity counts and the filter's
 * on/off states current - and a click on the copy is replayed on the original.
 */
function SceneControl({ node, className = "" }) {
  const hostRef = useRef(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || !node) return undefined;

    /* Every node in the control is given a name once, so a click on the copy
       can find the original it stands for. An index path would do until the
       view re-renders and the copy is replaced under the pointer; a name
       survives that. */
    const name = () => {
      let i = 0;
      if (!node.dataset.appfNode) node.dataset.appfNode = "root";
      node.querySelectorAll("*").forEach((el) => {
        if (el.dataset && !el.dataset.appfNode) el.dataset.appfNode = `n${i++}`;
      });
    };

    const sync = () => {
      /* naming writes attributes on the original, which is what this observer
         is watching - so it stops watching while it does it */
      observer.disconnect();
      name();
      const copy = node.cloneNode(true);
      copy.classList.remove("appf-in-header");
      host.replaceChildren(copy);
      observer.observe(node, { subtree: true, childList: true, attributes: true, characterData: true });
    };

    const observer = new MutationObserver(sync);
    sync();
    return () => observer.disconnect();
  }, [node]);

  /* A click on the copy is replayed on the node it stands for, where the view's
     own React is listening. */
  const replay = (event) => {
    if (!node) return;
    const clicked = event.target?.closest?.("[data-appf-node]");
    const id = clicked?.dataset?.appfNode;
    const target = !id || id === "root"
      ? node
      : node.querySelector(`[data-appf-node="${id}"]`) ?? node;

    const view = node.ownerDocument?.defaultView ?? window;
    target.dispatchEvent(new view.MouseEvent("click", { bubbles: true, cancelable: true }));
  };

  if (!node) return null;

  return <div ref={hostRef} className={`sb-proxy ${className}`} onClick={replay} />;
}

const SecurityBlast = (props) => {

  const currentProjectId = useProjectStore(s => s.currentProjectId);
  const isFirstRender = useRef(true);
  const [canShowData, setCanShowData] = useState(false)
  /* the scene's own controls, once the view has drawn them - see adoptSceneControls */
  const [sceneControls, setSceneControls] = useState({ perf: null, rail: null, info: null, help: null, severity: null, notice: null })
  const [gotVersion, setGotVersion] = useState(false)
  const [iframeVersion, setIframeVersion] = useState(Date.now())

  useEffect(() => {
    getItemDB(getProjectKey("viewType")).then( data => {
      if(data == "vulnerabilityData") {
        setCanShowData(true)
    }
  })
  }, [])

  // Dev-only: `localStorage.setItem("securityBlastMock", "true")` renders a 50,000-item
  // local vulnerability-blast fixture instead of querying the backend, for graph-performance testing.
  // `import.meta.env.DEV` keeps this path out of production builds so the large fixture chunk
  // is not emitted in `build/assets`.
  const securityBlastMockEnabled = import.meta.env.DEV && typeof window !== "undefined" && localStorage.getItem("securityBlastMock") === "true";
  const [securityBlastMockData, setSecurityBlastMockData] = useState(null);

  useEffect(() => {
    if (!import.meta.env.DEV || !securityBlastMockEnabled || securityBlastMockData) return;

    import("./securityBlastMock.json").then(m => {
      window._g.timeMachine = m.default.timeMachine;

        // Synthetic host metadata (tags/OS) so the 3D drill-in can aggregate hosts by
        // environment in mock mode, where the real hostsDiscoveryMap isn't built.
        const mockHostsMap = {};
        const envs = ["prod", "staging", "dev"];
        const oses = ["Ubuntu 22.04", "RHEL 9", "Debian 12"];
        m.default.timeMachine.forEach((h, i) => {
          mockHostsMap[h.id] = {
            name: h.name,
            machineId: h.id,
            tags: [envs[i % envs.length]],
            os: { name: oses[i % oses.length] },
          };
        });
        setItemDB(getProjectKey("hostsDiscoveryMap"), mockHostsMap);

      setSecurityBlastMockData(m.default);
    });
  }, [securityBlastMockEnabled, securityBlastMockData]);

  const { data: versionData, error: versionError, refetch: refetchVersion } = useQuery(gql`${_Q._Q_versionTime}`,
    {
      skip: props?.isInstalledSoftware || securityBlastMockEnabled,
      fetchPolicy: "network-only",
      errorPolicy: "all",
      notifyOnNetworkStatusChange: true
    });

  useEffect(() => {
    let isMounted = true;

    if (versionError) {
      console.error('SecurityBlast: Error fetching version data:', versionError);
      return;
    }

    if (versionData?.systems?.systems && versionData?.hosts?.hosts) {
      if (isMounted) {
        c_Fn.setVersionInfo({ systems: versionData.systems.systems, hosts: versionData.hosts.hosts });
        setGotVersion(true);
      }
    }

    return () => {
      isMounted = false;
    };
  }, [versionData, versionError]);

  const { data: vulnerabilityBlastDataQuery, refetch: refetchVulnerabilityBlast } = useQuery(gql`${_Q._Q_vulnerabilityBlast}`,
    {
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
      skip: !gotVersion || securityBlastMockEnabled
    });

  const vulnerabilityBlastData = securityBlastMockEnabled ? securityBlastMockData?.vulnerabilityBlastData : vulnerabilityBlastDataQuery;

  useEffect(() => {
    if (vulnerabilityBlastData) {
      setItemDB(getProjectKey("viewType"), "vulnerabilityData")
      // setItemDB("viewType", "hostData")
      let map = {};

      let res = { nodes: [], links: [] };
      let existingHosts = new Set()

      // The 3D graph now defaults to a clustered view (one bubble per package) and only
      // renders individual CVE/host nodes once a cluster is drilled into, so the full
      // dataset can be written here without needing to truncate it up front.
      let vulnerabilitiesToRender = vulnerabilityBlastData.vulnerabilities;

      vulnerabilitiesToRender.forEach(item => {
        let finItem = {};

        if (map[item.vulnerability.cve]) {
          finItem = {
            id: item.vulnerability.cve + " - " + item.vulnerability.version,
            name: item.vulnerability.cve + " - " + item.vulnerability.version,
            description: item.vulnerability.description,
            group: "vulnerability",
            nodeColor: 123123,
            val: item.affectedHosts.length,
            data: {
              affectedHosts: item.affectedHosts,
              vulnerability: {
                ...item.vulnerability,
                cve: item.vulnerability.cve + " - " + item.vulnerability.version
              }

            }
          }

          res.nodes.push(finItem);
        } else {
          finItem = {
            id: item.vulnerability.cve,
            name: item.vulnerability.cve,
            description: item.vulnerability.description,
            group: "vulnerability",
            nodeColor: 123123,
            val: item.affectedHosts.length,
            data: item
          }
          res.nodes.push(finItem);
          map[item.vulnerability.cve] = true;
        }

        let linkCount = 0
        for (let hostId of finItem.data.affectedHosts.filter((v, i, a) => a.indexOf(v) === i)) {
          linkCount++
          if (!existingHosts.has(hostId)) {
            existingHosts.add(hostId)
            res.nodes.push({
              id: hostId,
              name: window._g.timeMachine.find(host => host.id == hostId)?.name,
              group: "host"
            });
          }

          res.links.push({
            source: finItem.id,
            target: hostId,
          });

        }

      });

      let linksMap = {};

      res.links.map(l => {
        if (typeof linksMap[l.source + l.target] === 'undefined') {
          if (typeof linksMap[l.target + l.source] === 'undefined') {
            linksMap[l.source + l.target] = 1;
          } else {
            linksMap[l.target + l.source] += 1
          }
        } else {
          linksMap[l.source + l.target] += 1
        }
      })

      setItemDB(getProjectKey("vulnerabilityData"), res)
    }
  }, [vulnerabilityBlastData])

  useEnsureHosts();
  useEnsureApplications();
  useEnsureInstalledPackages();
  const storeHosts = useCoreStore(s => s.hosts);
  const storeApplications = useCoreStore(s => s.applications);
  const storeInstalledPackages = useCoreStore(s => s.installedPackagesAgg);

  useEffect(() => {
    // Mock mode writes its own synthetic hostsDiscoveryMap (see the mock-loading effect);
    // don't let real backend hosts overwrite it with entries that don't match mock host ids.
    if (securityBlastMockEnabled) return;
    if (!storeHosts.length) return;
    const hostsMap = {};

    storeHosts.forEach((host) => {
      hostsMap[host.machineId] = {
        name: host.name,
        machineId: host.machineId,
        timestamp: host.timestamp,
        tags: host.tags,
        os: host.os ? { name: host.os.name, eolDate: host.os.eolDate } : null,
        jvm: host.jvm ? { name: host.jvm.name, version: host.jvm.version, eolDate: host.jvm.eolDate } : null
      };
    });

    // Merge vulnerability data with severity counts from vulnerabilityBlastData
    if (vulnerabilityBlastData?.vulnerabilities) {
      vulnerabilityBlastData.vulnerabilities.forEach(item => {
        const severity = item.vulnerability.severity?.toUpperCase() || "UNKNOWN";

        item.affectedHosts.forEach(hostId => {
          if (hostsMap[hostId]) {
            if (!hostsMap[hostId].vulnerabilities) {
              hostsMap[hostId].vulnerabilities = { critical: 0, high: 0, medium: 0, low: 0, total: 0 };
            }

            hostsMap[hostId].vulnerabilities.total++;

            if (hostsMap[hostId].vulnerabilities[severity.toLowerCase()] !== undefined) {
              hostsMap[hostId].vulnerabilities[severity.toLowerCase()]++;
            }
          }
        });
      });
    }

    // Merge software/packages data
    storeInstalledPackages.forEach(h => {
      if (hostsMap[h.machineId]) {
        hostsMap[h.machineId].software = {
          totalInstalled: h.totalInstalled,
          eolPackages: h.eolPackages,
          candidatePackages: h.candidatePackages,
        };
      }
    });

    // Merge applications data
    storeApplications.forEach(app => {
      if (hostsMap[app.machineId]) {
        if (!hostsMap[app.machineId].applications) {
          hostsMap[app.machineId].applications = [];
        }
        hostsMap[app.machineId].applications.push({ name: app.name, version: app.version });
      }
    });

    setItemDB(getProjectKey("hostsDiscoveryMap"), hostsMap);

    // Notify the iframe about the update
    try {
      const iframe = document.querySelector('iframe');

      if (iframe?.contentWindow) {
        iframe.contentWindow.dispatchEvent(new CustomEvent('indexedDBUpdated', {
          detail: { key: getProjectKey("hostsDiscoveryMap"), value: hostsMap }
        }));
      }
    } catch (e) { /* cross-origin or iframe not ready */ }
  }, [storeHosts, storeInstalledPackages, storeApplications, vulnerabilityBlastData, securityBlastMockEnabled]);

  const { data: hostData, error: hostError, refetch: refetchHD } = useQuery(
    gql`${c_Fn.cleanUpQuery(_Q._Q_host).replaceAll(_Q._Q_qToken, props?.mid)
      .replaceAll(_Q._Q_qToken2, props.mid ? props.mid?.replaceAll("-", "_") : "")}`,
    {
      fetchPolicy: "cache-first",
      errorPolicy: "all",
      notifyOnNetworkStatusChange: true,
      skip: !props?.mid
    });

  useEffect(() => {
    let isMounted = true;

    if (hostError) {
      console.error('SecurityBlast: Error fetching host data:', hostError);
      return;
    }

    if (hostData) {
      const firstKey = Object.keys(hostData)[0];
      if (firstKey && isMounted) {
        // Data is fetched but not stored - only used for refetch
      }
    }

    return () => {
      isMounted = false;
    };
  }, [hostData, hostError]);

  const { data: systemData, error: systemError, refetch: refetchSD } = useQuery(
    gql`${c_Fn.cleanUpQuery(_Q._Q_system).replaceAll(_Q._Q_qToken, props?.mid)
      .replaceAll(_Q._Q_qToken2, props.mid ? props.mid?.replaceAll("-", "_") : "")}`,
    {
      fetchPolicy: "no-cache",
      errorPolicy: "all",
      notifyOnNetworkStatusChange: true,
      skip: !props?.mid
    });

  useEffect(() => {
    let isMounted = true;

    if (systemError) {
      console.error('SecurityBlast: Error fetching system data:', systemError);
      return;
    }

    if (systemData) {
      const firstKey = Object.keys(systemData)[0];
      if (firstKey && isMounted) {
        // Data is fetched but not stored - only used for refetch
      }
    }

    return () => {
      isMounted = false;
    };
  }, [systemData, systemError]);

  const iframeRef = useRef(null)
  useEffect(() => {
    checkUpdateNeeded("deploy", () => {
      refetchVersion();
      refetchSD()
      refetchHD()
      useCoreStore.getState().invalidate(['hosts', 'applications', 'installedPackages']);
      useCoreStore.getState().ensureHosts();
      useCoreStore.getState().ensureApplications();
      useCoreStore.getState().ensureInstalledPackages();
    })

    setTimeout(() => {
      iframeRef.current?.state?.cw.focus()
      iframeRef.current?.state?.cw.focus
    }, 200)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    activateMenu("security");
  }, []);

  // Refetch all data when the active project changes (skip initial mount)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const refetchAll = async () => {
      setCanShowData(false);
      setGotVersion(false);
      setID(uuidv4());
      setIframeVersion(Date.now());

      try {
        await refetchVersion();
        await refetchVulnerabilityBlast();
        // hosts/apps/packages are managed by coreStore — project switch triggers resetAll + fresh fetch
      } catch (e) {
        console.error("Error refetching data:", e);
      }
    };

    refetchAll();
  }, [currentProjectId, refetchVersion, refetchVulnerabilityBlast]);

  const { theme } = useContext(ThemeContext);

  const [dark, setDark] = useState(theme == "dark" ? true : false);
  const [id, setID] = useState("secframe");
  useEffect(() => {
    setID(uuidv4())
    setDark(theme == "dark" ? true : false)
  }, [theme]);


  const urlFor3d = `./3d/build/index.html`;

  /* The band floats over the scene, so the scene's own chrome - its toolbar and
     the two lists either side - has to start below it. The 3D view is a
     separate document, but a same-origin one, so the offset is written into it
     on load rather than guessed at from out here.

     Only the chrome moves: the canvas keeps the whole window, which is the
     point of running the iframe full-bleed. The panels size themselves off
     `100vh`, so each is capped by what the band leaves - and measured
     border-box, since they carry their own border and padding. */
  const SCENE_HEADER_INSET = 98; /* the band: 18px above it, 80px of card */

  const insetSceneChrome = (event) => {
    try {
      const doc = event?.target?.contentDocument;
      if (!doc?.head) return;

      let style = doc.getElementById("appf-header-inset");
      if (!style) {
        style = doc.createElement("style");
        style.id = "appf-header-inset";
        doc.head.appendChild(style);
      }

      style.textContent = `
        .sbClass > .noScroll { top: ${SCENE_HEADER_INSET}px !important; }
        .sbClass > .noScroll > div[style*="margin-top: 45px"],
        .sbClass > .noScroll > div[style*="margin-top: 117px"] {
          box-sizing: border-box !important;
        }
        .sbClass > .noScroll > div[style*="margin-top: 45px"] {
          max-height: calc(100vh - ${SCENE_HEADER_INSET + 45}px) !important;
        }
        .sbClass > .noScroll > div[style*="margin-top: 117px"] {
          max-height: calc(100vh - ${SCENE_HEADER_INSET + 117}px) !important;
        }
        /* The controls the band takes over, gone from the first frame rather
           than when the copy appears: matched here by where they sit - the
           toggle, the rail beside it, the view's own title after that, and the
           row that carries the save control - and again by class once
           adoptSceneControls has named them. */
        [title*="high performance"],
        [title*="high performance"] + div,
        [title*="high performance"] + div + div,
        [title*="high performance"] ~ .help,
        div:has(> .saveFilter),
        .appf-in-header { display: none !important; }

        /* the CVE list kept a 62px gutter for the rail that used to stand
           beside it; the rail is in the band now, so the list comes back to a
           23px inset from the page's edge - and its inner list keeps the 5px it
           always sat in from the panel */
        .sbClass > .noScroll > div[style*="margin-left: 62px"] { margin-left: 23px !important; }
        .sbClass > .noScroll > div[style*="left: 67px"] { left: 28px !important; }
      `;

      adoptSceneControls(doc);
    } catch (e) { /* the view is not ready, or is not ours to style */ }
  };

  /* The scene's own controls, shown in the band instead of over the canvas: the
     high performance toggle on the badge, the view rail before the refresh, the
     severity filter after it.

     They are found rather than rebuilt, because what each one does lives in the
     3D view and not here. The nodes stay where they are - their handlers are
     that document's React, delegated to its own root, so a node moved out here
     would draw but never respond - and the band carries a live copy of each.
     See SceneControl for how a click on the copy reaches the original. */
  const adoptSceneControls = (doc) => {
    let tries = 0;

    const look = () => {
      const perf = doc.querySelector('[title*="high performance"]');
      /* the rail sits beside the toggle, the view's own title after that, and
         the severity filter is the row that carries the save control */
      const rail = perf?.parentElement?.children?.[1] ?? null;
      const title = perf?.parentElement?.children?.[2] ?? null;
      /* the ⓘ beside that title, which is the half of it worth keeping: the
         name itself is already the band's */
      const info = title
        ? [...title.children].find((el) => !/security\s*blast/i.test(el.textContent || "")) ?? null
        : null;
      /* the tips the ⓘ used to open over the canvas: the band shows them on
         hover instead, so they come across too */
      const help = perf?.parentElement?.children?.[3] ?? doc.querySelector(".help") ?? null;
      const severity = [...doc.querySelectorAll("div")].find(
        (el) => el.querySelector(":scope > .saveFilter")
      ) ?? null;

      if (!perf || !rail || !info || !severity) {
        if (tries++ < 80) setTimeout(look, 250);
        return;
      }

      [perf, rail, title, help, severity].forEach((el) => el?.classList.add("appf-in-header"));
      copySceneStyles(doc);
      setSceneControls((prev) => ({ ...prev, perf, rail, info, help, severity }));
      watchForNotice(doc);
    };

    look();
  };

  /* The line the view writes while it draws - how many of the connections it
     is showing, and what to do about the rest. It belongs under the severity
     filter it qualifies, so the band takes it too.

     It comes and goes as the graph is drawn, and the view remakes the node
     rather than editing it, so this keeps watching and re-points at whatever
     is carrying the line now. */
  const watchForNotice = (doc) => {
    const find = () => [...doc.querySelectorAll("div")].find(
      (el) => !el.children.length && /connections/i.test(el.textContent || "")
    ) ?? null;

    const apply = () => {
      const notice = find();
      if (!notice) return;
      notice.classList.add("appf-in-header");
      setSceneControls((prev) => (prev.notice === notice ? prev : { ...prev, notice }));
    };

    apply();
    const host = doc.querySelector(".sbClass") ?? doc.body;
    if (!host) return;
    const observer = new MutationObserver(apply);
    observer.observe(host, { subtree: true, childList: true });
  };

  /* The copies in the band are styled by the 3D view's own rules, which live in
     its document. Only the rules these controls need come across, and each one
     is scoped to `.sb-proxy` on the way, so nothing else on this page is
     touched by a stylesheet written for another one. */
  const copySceneStyles = (doc) => {
    const WANTED = /railBtn|saveFilter|settings-icon|sevBtn|help/;

    /* The copies were laid out for the scene - the rail as a column pinned to
       the left edge, the filter as a full-width strip. In the band they are
       both a row of controls, so the box each one came in is undone first.
       !important because what it is undoing is inline. */
    let out = `
      .sb-proxy { display: inline-flex; align-items: center; }
      .sb-proxy > * {
        position: static !important;
        display: inline-flex !important;
        flex-direction: row !important;
        align-items: center !important;
        justify-content: flex-start !important;
        gap: 6px !important;
        width: auto !important;
        height: auto !important;
        max-width: none !important;
        margin: 0 !important;
        padding: 0 !important;
        top: auto !important;
        left: auto !important;
        right: auto !important;
        bottom: auto !important;
        background: transparent !important;
        border: 0 !important;
      }
      /* the rail's divider was a rule across the column; on a row it stands up */
      .sb-proxy--rail > * > div[style*="height: 1px"] {
        width: 1px !important;
        height: 18px !important;
        margin: 0 2px !important;
      }
      /* the ⓘ, beside the title: the band's own text colour and size rather
         than whatever it inherited over the canvas */
      .sb-proxy--info {
        margin-right: 10px;
        font-size: 16px;
        line-height: 1;
        color: var(--text-72);
      }
      .sb-proxy--info:hover { color: var(--accent-text); }
      .sb-proxy--info > * { cursor: help; }

      /* The tips, in the tooltip the ⓘ opens. The panel was laid out to float
         over the canvas - absolute, faded out until asked for - so here it is
         undone back into a block of text. This comes after the row reset
         above, which would otherwise have made a flex row of a paragraph and
         a list. */
      .sb-proxy--help > * {
        position: static !important;
        display: block !important;
        opacity: 1 !important;
        visibility: visible !important;
        transform: none !important;
        pointer-events: auto !important;
        width: auto !important;
        max-width: 460px !important;
        height: auto !important;
        max-height: none !important;
        margin: 0 !important;
        padding: 0 !important;
        text-align: left !important;
        background: transparent !important;
        border: 0 !important;
        box-shadow: none !important;
      }
      .sb-proxy--help ul { margin: 8px 0 0; padding-left: 18px; }
      .sb-proxy--help li { margin: 4px 0; line-height: 1.5; }

      .sb-help-tooltip {
        max-width: none !important;
        background: var(--card) !important;
        border: 1px solid var(--line);
        border-radius: 12px;
        box-shadow: 0 16px 40px rgba(0, 0, 0, 0.32);
        color: var(--text-90);
        font-size: 12.5px;
        padding: 14px 16px;
      }
      /* the filter and the line about it, as one block at the band's right edge */
      .sb-proxy-stack {
        display: inline-flex;
        flex-direction: column;
        align-items: flex-end;
        justify-content: center;
        gap: 2px;
        margin-left: auto;
        min-width: 0;
      }

      /* the chips came tracked out for a strip across the canvas; in the band
         they are read at a glance, so the letters close up */
      .sb-proxy--severity button {
        letter-spacing: 0 !important;
        font-size: 11px !important;
      }

      /* the drawing line, under the filter it qualifies */
      .sb-proxy--notice > * {
        display: block !important;
        position: static !important;
        max-width: 720px !important;
        width: auto !important;
        height: auto !important;
        margin: 0 !important;
        padding: 0 !important;
        background: transparent !important;
        border: 0 !important;
        border-radius: 0 !important;
        box-shadow: none !important;
        color: var(--text-55) !important;
        font-size: 11px !important;
        line-height: 1.3 !important;
        letter-spacing: 0 !important;
        text-align: right !important;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    `;

    for (const sheet of doc.styleSheets) {
      /* not our own: that one carries the rules hiding these very controls in
         the view, and copying it here would hide the copies too */
      if (sheet.ownerNode?.id === "appf-header-inset") continue;
      let rules;
      try { rules = sheet.cssRules; } catch (e) { continue; }
      for (const rule of rules) {
        if (!rule.selectorText || !WANTED.test(rule.selectorText)) continue;
        const scoped = rule.selectorText
          .split(",")
          .map((sel) => `.sb-proxy ${sel.trim()}`)
          .join(", ");
        out += `${scoped} { ${rule.style.cssText} }\n`;
      }
    }

    let style = document.getElementById("sb-proxy-styles");
    if (!style) {
      style = document.createElement("style");
      style.id = "sb-proxy-styles";
      document.head.appendChild(style);
    }
    style.textContent = out;
  };

  /* what the project-change effect does, on demand: ask for the data again and
     hand the view a new frame to draw it in */
  const ready = vulnerabilityBlastData != null || canShowData;
  const handleRefresh = async () => {
    setCanShowData(false);
    setGotVersion(false);
    setID(uuidv4());
    setIframeVersion(Date.now());
    try {
      await refetchVersion();
      await refetchVulnerabilityBlast();
    } catch (e) {
      console.error("Error refetching data:", e);
    }
  };

  /* The band every other page wears. Not on the installed-software view: that
     one is embedded in a page that carries its own.

     Here it floats over the scene rather than sitting in a row above it: the
     iframe is the page, so it runs the full height of the window and the band
     - frosted, so the scene reads through it - is laid over its top edge. */
  const band = props.isInstalledSoftware ? null : (
    <div
      className="page-header-toolbar orion-tokens"
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        /* over the iframe, which carries a z-index of its own */
        zIndex: 1001,
      }}
    >
      <div>
        <PageHeader
          className="page-header--blast"
          mark={NavSecurity}
          /* the scene's high performance toggle, now on the badge: the icon it
             used to have over the canvas is hidden, and this stands in for it */
          markTitle="Click to toggle high performance mode"
          onMarkClick={() => sceneControls.perf?.click()}
          title="Security Blast"
          /* what the scene is showing: the hosts in it, and the CVEs blowing
             through them - counted off the same payload the graph is built
             from, so the band and the canvas cannot disagree */
          eyebrow={[
            `${fmtCount(storeHosts?.length)} hosts`,
            `${fmtCount(vulnerabilityBlastData?.vulnerabilities?.length)} cves`,
          ]}
          loading={!ready}
          actions={(
            <>
              <Tooltip
                placement="bottom-start"
                enterDelay={200}
                title={<SceneControl node={sceneControls.help} className="sb-proxy--help" />}
                componentsProps={{ tooltip: { className: "sb-help-tooltip orion-tokens" } }}
              >
                <span>
                  <SceneControl node={sceneControls.info} className="sb-proxy--info" />
                </span>
              </Tooltip>
              <SceneControl node={sceneControls.rail} className="sb-proxy--rail" />
              <PageHeaderRefresh loading={!ready} onClick={handleRefresh} />
              <span className="sb-proxy-stack">
                <SceneControl node={sceneControls.severity} className="sb-proxy--severity" />
                <SceneControl node={sceneControls.notice} className="sb-proxy--notice" />
              </span>
            </>
          )}
        />
      </div>
    </div>
  );

  return (<>
  {
  props.isInstalledSoftware?<>
  <div style={{
    width: "calc(100%)",
    height: props.isInstalledSoftware?"calc(100vh - 64px)":"100%" 
  }}
  >
    <IFrame
      key={id}
      ref={iframeRef}
      style={{
        marginLeft: 0,
        zIndex: 1000,
        background: dark ? "var(--background-gradient-main)" : "#FFFFFF",
      }}
      src={`${urlFor3d}?v=${iframeVersion}&installedSoftware=1`}
      width="100%" 
      height={"100%" }
      />
      </div>
      </>
      :
    ready ? (
      <div style={{
        /* the band above is positioned against this box */
        position: "relative",
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        borderLeft: `${dark ? "1" : "0"}px solid rgba(255,255,255,0.081)`,
      }}>
        {band}
        <IFrame
          key={id}
          ref={iframeRef}
          style={{
            marginLeft: 0,
            zIndex: 1000,
            flex: "1 1 auto",
            minHeight: 0,
            background: dark ? "var(--background-gradient-main)" : "#FFFFFF",
            border: "0px solid red",
          }}
          onLoad={insetSceneChrome}
          src={`${urlFor3d}?v=${iframeVersion}`} width="100%" height="100%" />
      </div>)
      : (
        <div
          style={{
            position: 'relative',
            borderLeft: `${dark ? "1" : "0"}px solid rgba(255,255,255,0.081)`,
            width: '100%',
            minWidth: '650px',
            height: '100%',
            overflow: 'hidden',
          }}
        >
          {band}
          <div
            className="dndflowloading"
            style={{
              zIndex: 99999,
              position: 'absolute',
              top: "calc(50% - 100px)",
              left: "calc(50% - 200px)",
              fontSize: 28,
              width: '200px',
              color: "#C02B11",
              height: '30px',
              // border: '1px solid red'
            }}
          >
            <div
              style={{
                position: 'absolute',
                fontSize: 28,
                marginTop: 22,
                marginLeft: 'calc(50% - 200px)',
                width: '400px',
                color: !dark ? "var(--background-gradient-main)" : "#FFFFFF55",
                height: '30px',
              }}
            >Loading data ...</div>
            {loadingProgress}
          </div></div>)}
  </>)
};

export default (props) =><SecurityBlast isInstalledSoftware={props.isInstalledSoftware} />;